import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GenericTable from '../../../common/GenericTable'
import SearchWithFilter from '../../../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../../../common/FiltersModal'
import EmptyPageResults from '../../../common/EmptyPageResults'
import LoadingSpinner from '../../../common/LoadingSpinner'
import Dropdown from '../../../common/Dropdown'
import SimpleButton from '../../../common/SimpleButton'
import ActionsMenu from '../../../common/ActionsMenu'
import { toast } from '../../../common/Toast'
import TooltipedIcon from '../../../common/TooltipedIcon'
import { DownloadIcon, FilterTableIcon, InformationIcon, MenuKebabVerticalBackgroundIcon, MenuKebabVerticalIcon } from '../../../../icons'
import { downloadFileUTF8, getLastNYearsRangeISO } from '../../../../utils/functions'
import { useWindowSize } from '../../../../libs/hooks'
import countries from '../../../../utils/countries.json'
import { useDeepCompareEffect } from '../../../../hooks/useDeepCompareEffect'
import { loadModels, loadRegions } from '../../../../actions/shared'
import { getAccounts } from '../../../../utils/requests/accountManagementAPI'
import {
  clearReportsMessages,
  downloadRemoteAccessConnectionData,
  loadRemoteAccessConnectionRawData,
  loadRemoteAccessConnectionStatisticsData,
  loadConnectedDevicesCountries,
} from '../../../../actions/reports'
import RemoteAccessReportChart from './RemoteAccessReportChart'
import ViewRemoteAccessModal from './ViewRemoteAccessModal'
import '../../internal-reports.scss'

const { startISO: defaultStartISO, endISO: defaultEndISO, timeZone } = getLastNYearsRangeISO(5)

const RemoteAccessConnectionReports = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [connectionTableData, setConnectionTableData] = useState([])
  const [connectionChartData, setConnectionChartData] = useState([])
  const [startDateFilter, setStartDateFilter] = useState(defaultStartISO)
  const [endDateFilter, setEndDateFilter] = useState(defaultEndISO)
  const [selectedFilterModel, setSelectedFilterModel] = useState([])
  const [selectedFilterAccount, setSelectedFilterAccount] = useState([])
  const [selectedFilterCountry, setSelectedFilterCountry] = useState([])
  const [selectedFilterRegion, setSelectedFilterRegion] = useState([])
  const [orderBy, setOrderBy] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [noPing, setNoPing] = useState(false)
  const [expandedDevice, setExpandedDevice] = useState(0)
  const [chartType, setChartType] = useState('stackedBar')
  const [chartFrequency, setChartFrequency] = useState('year')
  const [accounts, setAccounts] = useState([])
  const [showChartFiltersModal, setShowChartFiltersModal] = useState(false)
  const [rowId, setRowId] = useState('null')
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const PAGE_SIZE = 10
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const UNKNOWN_LABEL = t('unknown', { defaultValue: 'Unknown' })

  const orderingFields = [
    {
      field: 'date',
      order: 'asc',
      translationKey: 'date_asc',
    },
    {
      field: 'date',
      order: 'desc',
      translationKey: 'date_desc',
    },
    {
      field: 'model',
      order: 'asc',
      translationKey: 'model_asc',
    },
    {
      field: 'model',
      order: 'desc',
      translationKey: 'model_desc',
    },
    {
      field: 'accountType',
      order: 'asc',
      translationKey: 'account_type_asc',
    },
    {
      field: 'accountType',
      order: 'desc',
      translationKey: 'account_type_desc',
    },
    {
      field: 'total',
      order: 'asc',
      translationKey: 'total_sessions_asc',
    },
    {
      field: 'total',
      order: 'desc',
      translationKey: 'total_sessions_desc',
    },
    // {
    //   field: 'totalUniqueUsers',
    //   order: 'asc',
    //   translationKey: 'total_unique_users_asc',
    // },
    // {
    //   field: 'totalUniqueUsers',
    //   order: 'desc',
    //   translationKey: 'total_unique_users_desc',
    // },
    // {
    //   field: 'averageSessionTime',
    //   order: 'asc',
    //   translationKey: 'average_session_time_asc',
    // },
    // {
    //   field: 'averageSessionTime',
    //   order: 'desc',
    //   translationKey: 'average_session_time_desc',
    // },
  ]

  const chartTypeKeys = ['line', 'bar', 'stackedBar', 'pie']
  const chartFrequencyKeys = ['month', 'quarter', 'year']
  const chartFilterOptions = [
    {
      name: t('reports.frequency'),
      type: FilterType.singleSelect,
      dataset: chartFrequencyKeys,
      input: chartFrequency,
      output: (selected) => setChartFrequency(selected),
      required: true,
    },
    {
      name: t('reports.chart_type'),
      type: FilterType.singleSelect,
      dataset: chartTypeKeys,
      input: chartType,
      output: (selected) => setChartType(selected),
      required: true,
    },
  ]

  const allowedAccounts = ['Coltene', 'Dealer']

  const renderTooltipText = (property) => {
    switch (property) {
      case 'model':
        return (
          <div className="tooltip-text">
            <p className="tooltip-body">{t('reports.unknown_model_message')}</p>
          </div>
        )
      case 'accountType':
        return (
          <div className="tooltip-text">
            <p className="tooltip-body">{t('reports.unknown_account_type_message')}</p>
          </div>
        )
      case 'totalUniqueUsers':
        return (
          <div className="tooltip-text">
            <p className="tooltip-body">{t('reports.unknown_total_unique_users_message')}</p>
          </div>
        )
      case 'averageSessionTime':
        return (
          <div className="tooltip-text">
            <p className="tooltip-body">{t('reports.unknown_average_session_time_message')}</p>
          </div>
        )
    }
  }

  const renderUnknownCell = (property) => {
    return (
      <div className="info-icon">
        {UNKNOWN_LABEL} <TooltipedIcon icon={<InformationIcon />} tooltipText={renderTooltipText(property)} orientation={isMobile ? 'left' : 'right'} />
      </div>
    )
  }

  useEffect(() => {
    if (props.remoteAccessConnectionStatisticsData.length === 0) {
      props.actions.loadRemoteAccessConnectionStatisticsData()
    }
  }, [props.actions])

  useEffect(() => {
    const getAccountsData = async () => {
      const allAccounts = await getAccounts()
      const remoteAccessAccounts = allAccounts.filter((account) => allowedAccounts.map((e) => e.toLowerCase()).includes(account.name.toLowerCase()))
      setAccounts(remoteAccessAccounts)
    }
    getAccountsData()
  }, [])

  useEffect(() => {
    if (props.deviceModels.length === 0) {
      props.actions.loadModels()
    }

    if (props.geographicalConnectionCountries.length === 0) {
      props.actions.loadConnectedDevicesCountries()
    }

    if (props.reportsRegions.length === 0) {
      props.actions.loadRegions()
    }
  }, [props.actions])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadData(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    if (!shouldApplyFilters) return
    const t = window.setTimeout(() => {
      setSelectedPage(1)
      handleLoadData(true)
    })
    return () => clearTimeout(t)
  }, [selectedFilterCountry, selectedFilterModel, selectedFilterRegion, startDateFilter, endDateFilter, selectedFilterAccount, orderBy, shouldApplyFilters])

  useEffect(() => {}, [orderBy])

  useEffect(() => {
    const chartData = props.remoteAccessConnectionStatisticsData.map((device, index) => ({
      index,
      year: device.year,
      month: device.month,
      quarter: device.month ? Math.ceil(device.month / 3) : null,
      accountType: device.account ? device.account : UNKNOWN_LABEL,
      total: device.count,
    }))
    setConnectionChartData(chartData)
    const tableData = props.remoteAccessConnectionStatisticsData.map((device, index) => {
      const row = {
        id: index,
        date: `${device.year}-${device.month ? (device.month < 10 ? '0' + device.month : device.month) : '00'}`,
        model: device.model ? device.model : renderUnknownCell('model'),
        originalModel: device.model,
        accountType: device.account ? device.account : renderUnknownCell('accountType'),
        total: device.count,
        totalUniqueUsers: device.totalUniqueUsers || renderUnknownCell('totalUniqueUsers'),
        averageSessionTime: device.averageSessionTime || renderUnknownCell('averageSessionTime'),
        userGroup: device.userGroup,
        countries: device.countries.map((c) => ({
          name: countries.find((country) => country.code === c.countryCode)?.name || 'Unknown',
          total: c.count,
        })),
      }
      row.actions = [
        {
          label: 'view_details',
          onActionClick: (e) => {
            e?.stopPropagation?.()
            setShowActionsMenu(null)
            handleOpenViewModal(row)
          },
        },
      ]
      return row
    })
    let queryParams = createQueryParams()
    if (queryParams.orderBy === 'date') {
      if (queryParams.order === 'desc') {
        setConnectionTableData(connectionTableData.sort((a, b) => b.date.localeCompare(a.date, undefined, { numeric: true })))
      } else {
        setConnectionTableData(connectionTableData.sort((a, b) => a.date.localeCompare(b.date, undefined, { numeric: true })))
      }
    } else {
      setConnectionTableData(tableData)
    }
  }, [props.remoteAccessConnectionStatisticsData, orderBy])

  const createQueryParams = () => {
    let queryParams = {}

    if (startDateFilter) {
      queryParams = { ...queryParams, startDate: startDateFilter }
    }

    if (endDateFilter) {
      queryParams = { ...queryParams, endDate: endDateFilter }
    }

    if (startDateFilter && endDateFilter) {
      queryParams = { ...queryParams, timezone: timeZone }
    }

    if (selectedFilterModel.length > 0) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterAccount.length > 0) {
      const accountTypeIds = selectedFilterAccount
        .map((name) => accounts.find((t) => t.name?.toLowerCase() === name.toLowerCase())?.id)
        .filter((id) => Number.isInteger(id))

      if (accountTypeIds.length > 0) {
        queryParams = { ...queryParams, accountId: accountTypeIds }
      }
    }

    if (selectedFilterCountry.length > 0) {
      const countryCodes = props.geographicalConnectionCountries
        .filter((c) => selectedFilterCountry.includes(c.name))
        .map((c) => c.countryCode)
        .filter(Boolean)

      if (countryCodes.length) {
        queryParams = { ...queryParams, country: countryCodes }
      }
    }

    if (selectedFilterRegion.length > 0) {
      queryParams = { ...queryParams, region: selectedFilterRegion }
    }

    if (noPing) {
      queryParams = { ...queryParams, noPing: true }
    }

    if (orderBy?.field && orderBy?.order) {
      orderBy.field = orderBy.field === 'accountType' ? 'account' : orderBy.field
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const handleLoadData = (withReset) => {
    let queryParams = createQueryParams()
    queryParams = {
      ...queryParams,
    }

    if (queryParams?.orderBy && queryParams.orderBy === 'date') {
      delete queryParams['orderBy']
      delete queryParams['order']
    }

    props.actions.loadRemoteAccessConnectionStatisticsData(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleResetFilters = () => {
    setStartDateFilter(null)
    setEndDateFilter(null)
    setSelectedFilterModel([])
    setSelectedFilterAccount([])
    setSelectedFilterCountry([])
    setSelectedFilterRegion([])
    setNoPing(false)
    setVisibleCount(PAGE_SIZE)
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
    setVisibleCount(PAGE_SIZE)
  }

  const handleResetChartFilters = () => {
    setChartFrequency('')
    setChartType('')
  }

  const handleDownload = () => {
    let queryParams = createQueryParams()

    if (queryParams?.orderBy && (queryParams.orderBy === 'total' || queryParams.orderBy === 'date')) {
      delete queryParams['orderBy']
      delete queryParams['order']
    }

    props.actions.downloadRemoteAccessConnectionData(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')

      downloadFileUTF8({
        data: res.value,
        fileName: `connected-devices_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }

  const getChartData = (chartFrequency) => {
    const chartData = connectionChartData.reduce((acc, record) => {
      let dateKey = ''
      let sortKey = 0

      if (chartFrequency === 'year') {
        const y = Number(record.year) || 0
        dateKey = y ? String(y) : ''
        sortKey = y
      } else if (chartFrequency === 'quarter') {
        const y = Number(record.year) || 0
        const q = record.quarter ?? (record.month ? Math.ceil(record.month / 3) : 1)
        dateKey = y ? `${y}-Q${q}` : ''
        sortKey = y * 10 + q
      } else if (chartFrequency === 'month') {
        const y = Number(record.year) || 0
        const m = record.month ?? 1
        const mm = m < 10 ? '0' + m : m
        dateKey = y ? `${y}-${mm}` : ''
        sortKey = y * 100 + m
      } else {
        const y = Number(record.year) || 0
        dateKey = y ? String(y) : ''
        sortKey = y
      }

      if (!dateKey) return acc

      if (!acc[dateKey]) {
        acc[dateKey] = { date: dateKey }
        Object.defineProperty(acc[dateKey], 'sortKey', { value: sortKey, enumerable: false })
      }
      acc[dateKey][record.accountType] = (acc[dateKey][record.accountType] || 0) + record.total
      return acc
    }, {})

    return Object.values(chartData).sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0))
  }

  const renderChartSection = () => {
    return (
      <div className={`chart-section ${chartType === 'stackedBar' ? 'stackedBar-chart' : ''}`}>
        <div className="chart-filters-row">
          <div className="chart-title">{t('reports.remote_access_sessions_overview')}</div>
          <SimpleButton className="filter-button" onClick={() => setShowChartFiltersModal(true)}>
            <div className="icon-wrapper">
              <FilterTableIcon />
            </div>
            {t('reports.chart_styles')}
          </SimpleButton>
        </div>
        <RemoteAccessReportChart data={getChartData(chartFrequency) || []} chartType={chartType} />
        {showChartFiltersModal && (
          <div className="chart-filters-modal-container">
            <FiltersModal
              filters={chartFilterOptions}
              name="styles"
              closeFilters={() => setShowChartFiltersModal(false)}
              modalClassName="chart-filters-modal"
              resetFilters={handleResetChartFilters}
              requireAllRequiredFilters
            />
          </div>
        )}
      </div>
    )
  }

  const renderDevicesTable = () => {
    const rowsToRender = connectionTableData.slice(0, visibleCount)
    if (props.isStatisticsDataLoading) {
      return (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )
    }

    if (!props.isStatisticsDataLoading && !isFirstLoad && connectionTableData.length === 0) {
      return <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
    }

    if (!isMobile && rowsToRender.length > 0) {
      return (
        <>
          <GenericTable
            data={rowsToRender}
            headers={[
              {
                title: t('model'),
                orderKey: 'model',
              },
              {
                title: t('date'),
                orderKey: 'date',
              },
              {
                title: t('account_context.account_type'),
                orderKey: 'accountType',
              },
              {
                title: t('reports.total_sessions'),
                orderKey: 'total',
              },
              {
                title: t('reports.total_unique_users'),
                // orderKey: 'totalUniqueUsers',
              },
              {
                title: `${t('reports.average_session_time')}`,
                // orderKey: 'averageSessionTime',
              },
              { title: '' },
            ]}
            keys={['model', 'date', 'accountType', 'total', 'totalUniqueUsers', 'averageSessionTime', 'action']}
            keyRenderer={{
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item.id)
                    }}
                  >
                    {showActionsMenu === item.id ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item.id && (
                      <ActionsMenu
                        setHideMenu={() => setShowActionsMenu(null)}
                        actions={item.actions.map((a) => ({
                          ...a,
                          onActionClick: (e) => {
                            e?.stopPropagation?.()
                            setShowActionsMenu(null)
                            a.onActionClick(e)
                          },
                        }))}
                        isLoading={props.isLoading}
                      />
                    )}
                  </div>
                )
              },
              averageSessionTime: (item) => {
                if (typeof item.averageSessionTime === 'object') {
                  return item.averageSessionTime
                }
                return formatSecondsToTime(item.averageSessionTime)
              },
            }}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            onRowClick={(row) => handleOpenViewModal(row)}
            isLoading={props.isStatisticsDataLoading}
          />
          {visibleCount < connectionTableData.length && (
            <div className="buttons-wrapper">
              <SimpleButton
                className="action-button"
                onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, connectionTableData.length))}
                disabled={props.isStatisticsDataLoading}
              >
                {t('load_more', { defaultValue: 'Load more' })}
              </SimpleButton>
            </div>
          )}
        </>
      )
    }

    if (isMobile) {
      return (
        <>
          {connectionTableData.length > 0 && (
            <div className="order-by-mobile-wrapper">
              <Dropdown
                className="dropdown-input"
                placeholder={t('select_ordering')}
                options={orderingFields.map((field) => ({
                  key: field.translationKey,
                  value: t(`sortable_context.${field.translationKey}`),
                }))}
                initialSelectedKey={orderBy?.translationKey}
                onChange={(orderBy) => {
                  const sortOption = orderingFields.find((f) => f.translationKey === orderBy.key)
                  handleSort(sortOption)
                }}
              />
            </div>
          )}

          {rowsToRender.map((device, index) => (
            <div key={index} className="card" onClick={() => setExpandedDevice(index)}>
              <div className={'card-item' + (expandedDevice !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('model')}</div>
                <div className={'card-item-body' + (expandedDevice !== index ? ' align-center' : '')}>
                  <div>{device.model}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(device.id)
                    }}
                  >
                    {showActionsMenu === device.id ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === device.id && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={device.actions} isLoading={props.isTableLoading} />
                    )}
                  </div>
                </div>
              </div>
              {expandedDevice === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('date')}</div>
                    <div className="card-item-body">{device.date}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('account_context.account_type')}</div>
                    <div className="card-item-body">{device.accountType}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('reports.total_sessions')}</div>
                    <div className="card-item-body">{device.total}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('reports.total_unique_users')}</div>
                    <div className="card-item-body">{device.totalUniqueUsers || '-'}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{`${t('reports.average_session_time')}`}</div>
                    <div className="card-item-body">{formatSecondsToTime(device.averageSessionTime) || '-'}</div>
                  </div>
                </>
              )}
            </div>
          ))}
          {visibleCount < connectionTableData.length && (
            <div className="buttons-wrapper">
              <SimpleButton
                className="action-button"
                onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, connectionTableData.length))}
                disabled={props.isStatisticsDataLoading}
              >
                {t('load_more', { defaultValue: 'Load more' })}
              </SimpleButton>
            </div>
          )}
        </>
      )
    }
  }

  const renderTableSection = () => (
    <div className="table-section">
      <div className="actions-wrapper">
        <div className="table-actions-row">
          <SimpleButton className="action-button" onClick={() => handleDownload()} disabled={props.isDownloadLoading || connectionTableData.length === 0}>
            <DownloadIcon />
            {t('download')}
          </SimpleButton>
        </div>
      </div>
      {renderDevicesTable()}
    </div>
  )

  const handleOpenViewModal = (row) => {
    setRowId(row.id)
    setSelectedRecord(row)
    setShowViewModal(true)
  }
  const closeModal = () => {
    setShowViewModal(false)
    setSelectedRecord(null)
  }
  const toggleShowActionsMenu = (id) => {
    setShowActionsMenu(showActionsMenu === id ? null : id)
  }

  const formatSecondsToTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00:00'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="internal-reports-section geographical-connection">
      <div className="actions-wrapper page-section">
        <SearchWithFilter
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={
            (startDateFilter && endDateFilter) ||
            selectedFilterModel.length > 0 ||
            selectedFilterAccount.length > 0 ||
            selectedFilterCountry.length > 0 ||
            selectedFilterRegion.length > 0
          }
          counterBadge={
            (startDateFilter && endDateFilter ? 1 : 0) +
            selectedFilterModel.length +
            selectedFilterAccount.length +
            selectedFilterCountry.length +
            selectedFilterRegion.length
          }
        />
      </div>
      <div className="disclaimer-box-text">
        <div className="icon-wrapper">
          <InformationIcon />
        </div>
        <div className="disclaimer-wrapper">
          <div className="title">{t('reports.date_disclaimer')}</div>
        </div>
      </div>
      {!props.isStatisticsDataLoading && !isFirstLoad && connectionTableData.length === 0 ? (
        <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
      ) : (
        <>
          {renderChartSection()}
          {renderTableSection()}
        </>
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('date'),
              type: FilterType.dateRange,
              dataset: null,
              input: { startDate: startDateFilter, endDate: endDateFilter },
              output: (startDate, endDate) => {
                setStartDateFilter(startDate)
                setEndDateFilter(endDate)
              },
              quickLinks: [
                { label: t('1_month'), months: 1, onClick: () => setNoPing(false) },
                { label: t('3_months'), months: 3, onClick: () => setNoPing(false) },
                { label: t('6_months'), months: 6, onClick: () => setNoPing(false) },
                { label: t('12_months'), months: 12, onClick: () => setNoPing(false) },
              ],
            },
            {
              name: t('model'),
              type: FilterType.multiSelect,
              dataset: props.deviceModels.sort((a, b) => a.localeCompare(b)),
              input: selectedFilterModel,
              output: (model) => setSelectedFilterModel(model),
            },
            {
              name: t('account'),
              type: FilterType.multiSelect,
              dataset: accounts.map((account) => account.name),
              input: selectedFilterAccount,
              output: (account) => setSelectedFilterAccount(account),
              // output: (account) =>
              //   setSelectedFilterAccount((prev) => {
              //     const next = Array.isArray(account) ? account : []
              //     const changed = next.length !== prev.length || next.some((v, i) => v !== prev[i])
              //     if (changed) setShouldApplyFilters(true)
              //     return next
              //   }),
            },
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: props.geographicalConnectionCountries.map((c) => c.name).sort((a, b) => a.localeCompare(b)),
              input: selectedFilterCountry,
              output: (country) => setSelectedFilterCountry(country),
            },
            {
              name: t('region'),
              type: FilterType.multiSelect,
              dataset: props.reportsRegions.map((region) => region.name).sort((a, b) => a.localeCompare(b)),
              input: selectedFilterRegion,
              output: (region) => setSelectedFilterRegion(region),
            },
          ]}
          shouldFilter={(shouldApply) => {
            setShouldApplyFilters(shouldApply)
          }}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {props.isStatisticsDataLoading && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
      {showViewModal && <ViewRemoteAccessModal tableData={connectionTableData} rowId={rowId} closeModal={closeModal} />}
    </div>
  )
}

function stateToProps({ reports, shared }) {
  return {
    remoteAccessConnectionRawData: reports?.remoteAccessConnectionRawData || [],
    remoteAccessConnectionStatisticsData: reports?.remoteAccessConnectionStatisticsData || [],
    geographicalConnectionCountries: reports?.connectedDevicesCountries || [],
    deviceModels: [
      ...new Set(
        shared?.models
          ?.filter((m) => m.ocp_access && (m.is_g4_plus || m.is_g4) && !m.model.toLowerCase().includes('bravo') && !m.model.toLowerCase().includes('jeni'))
          ?.map((model) => model.model) || [],
      ),
    ],
    reportsRegions: shared?.regions || [],
    isStatisticsDataLoading: reports?.isStatisticsDataLoading,
    isDownloadLoading: reports?.isDownloadLoading,
    errorMessage: reports?.errorMessage,
    successMessage: reports?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadRemoteAccessConnectionRawData,
        loadRemoteAccessConnectionStatisticsData,
        loadConnectedDevicesCountries,
        downloadRemoteAccessConnectionData,
        clearReportsMessages,
        loadRegions,
        loadModels,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(RemoteAccessConnectionReports)

